// Theme switcher for the ALPHA-06 bartender ticket UI, wired against the
// two-layer theme architecture design/README.md and design/tokens.css
// describe: base tokens load once, then exactly one theme file, then
// `data-theme` on the root element selects the active scheme. This module
// owns swapping which theme <link> is loaded and which `data-theme` value
// is stamped. UX21 (DEC-117) ended the earlier device-local persistence:
// the product default applies pre-login and post-logout on every device,
// and a signed-in user's own choice is stored server-side on their
// profile (main.ts's setTheme/handleAccountThemeChange), never in
// localStorage.
//
// Per design/README.md: "A location's chosen scheme is configuration, not
// code." This module has no per-shop value baked in; THEME_STORAGE_KEY
// only names a browser-storage slot, and DEFAULT_THEME_ID is
// the product default (since UX83, "snipsip-ink"; before it, "brass", per
// design/README.md's old "brass.css: default, current"). "Scissors & Scotch" is the shipped
// curated theme's own display name as of the 2026-08-21 theming
// directive (product owner-approved, not an inferred choice by this
// module) and any location, including a different franchise, may still
// pick it or any other curated theme; the set of options is product
// configuration, not a per-shop override.

export interface ThemeOption {
  id: string;
  label: string;
  /** File under design/themes/, e.g. "brass.css". */
  file: string;
}

// Order matches design/README.md's own listing: the curated brand
// schemes first (the two SnipSip schemes, then Scissors & Scotch), then the
// two accessibility schemes.
//
// The "brass" id and file stay as-is after the product owner's rename to
// "Scissors & Scotch" (Amendment 2026-08-21 (eighth) to
// docs/superpowers/plans/2026-08-19-alpha-work-breakdown.md): only the
// LABEL shown to a user changes. The id is the stable key this module
// resolves to a stylesheet file (applyTheme below) and the value stored
// in users.default_theme_id; neither is ever shown to a user, so renaming
// either would buy nothing. See brass.css's own file header for the same
// tradeoff recorded at the CSS layer.
export const THEME_OPTIONS: readonly ThemeOption[] = [
  { id: "snipsip-ink", label: "Snip & Sip Ink", file: "snipsip-ink.css" },
  { id: "snipsip-site", label: "Snip & Sip Light", file: "snipsip-site.css" },
  { id: "brass", label: "Scissors & Scotch", file: "brass.css" },
  { id: "charcoal", label: "Charcoal", file: "charcoal.css" },
  { id: "notebook", label: "Notebook", file: "notebook.css" },
  { id: "high-contrast", label: "High Contrast", file: "high-contrast.css" },
  { id: "colorblind", label: "Colorblind Safe", file: "colorblind.css" },
];

//
// UX83 (owner ruling 2026-10-09, "For brand direction use the designers
// recommendation"): the product default is "snipsip-ink", used pre-login,
// post-logout, and for any user whose users.default_theme_id is NULL.
// No stored choice is rewritten: a user, location or tenant that picked
// "brass" (Scissors & Scotch) or any other id keeps it, and no migration
// touches users.default_theme_id. This supersedes the UX21 (DEC-117)
// default of "brass" for the default only; "brass" is still a curated
// option anyone can pick in My Profile. The server names no default; it
// only validates ids (AUTH_THEME_IDS in src/domain/users.ts).
export const DEFAULT_THEME_ID = "snipsip-ink";

// UX21 (DEC-117): no longer written to. Kept only to name the key
// clearStoredThemeId removes, so a value an earlier build left on a real
// device does not sit forever as dead state.
const THEME_STORAGE_KEY = "bms-ui-theme";

// Removes any theme id an earlier build stored on this device. Called
// unconditionally on every load (init(), main.ts): a no-op after the
// first call on a given device, since the key is already gone by then.
export function clearStoredThemeId(): void {
  window.localStorage.removeItem(THEME_STORAGE_KEY);
}

// Applies a theme by swapping the single theme <link>'s href (identified
// by data-theme-link on the element, set once in index.html) and setting
// data-theme on <html>. Only ever one theme file is loaded at a time, per
// design/README.md: "Load tokens.css once, then load exactly one theme
// file."
export function applyTheme(themeId: string, themeLinkElement: HTMLLinkElement): void {
  const option = THEME_OPTIONS.find((candidate) => candidate.id === themeId);
  const resolved = option ?? THEME_OPTIONS.find((candidate) => candidate.id === DEFAULT_THEME_ID);
  if (resolved === undefined) {
    throw new Error("no default theme option configured");
  }
  themeLinkElement.href = `/design/themes/${resolved.file}`;
  document.documentElement.dataset["theme"] = resolved.id;
}
